import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TablePagination,
  TableSortLabel,
  Menu,
  Snackbar,
  Autocomplete,
  createFilterOptions,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  GetApp as GetAppIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { simsAPI, providersAPI, devicesAPI } from '../services/api';
import ImportProgressDialog from '../components/ImportProgressDialog';

function SimsPage() {
  const [sims, setSims] = useState([]);
  const [providers, setProviders] = useState([]);
  const [devices, setDevices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSim, setEditingSim] = useState(null);
  const [orderBy, setOrderBy] = useState('iccid');
  const [order, setOrder] = useState('asc');
  const [importAnchorEl, setImportAnchorEl] = useState(null);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [templateAnchorEl, setTemplateAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importJobId, setImportJobId] = useState(null);
  const [formData, setFormData] = useState({
    iccid: '',
    sim_number: '',
    provider: null, // Will store provider object
    status: 'available',
    device: null, // Will store device object
    notes: '',
  });

  useEffect(() => {
    loadSims();
  }, [page, rowsPerPage, searchTerm, providerFilter, statusFilter, order, orderBy]);

  useEffect(() => {
    loadProviders();
    loadDevices();
  }, []);

  const loadSims = async () => {
    try {
      setLoading(true);
      const params = { 
        page: page + 1, 
        page_size: rowsPerPage 
      };
      
      // Add search filter
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      // Add provider filter
      if (providerFilter && providerFilter !== 'all') {
        params.provider = providerFilter;
      }
      
      // Add status filter
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      // Add ordering
      if (orderBy) {
        params.ordering = order === 'desc' ? `-${orderBy}` : orderBy;
      }
      
      const response = await simsAPI.getAll(params);
      // Handle paginated response
      if (response.data.results) {
        setSims(response.data.results);
        setTotalCount(response.data.count);
      } else {
        setSims(response.data);
        setTotalCount(response.data.length);
      }
      setError(null);
    } catch (err) {
      console.error('Error loading SIMs:', err);
      setError('Failed to load SIM cards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadProviders = async () => {
    try {
      const response = await providersAPI.getAll();
      setProviders(response.data.results || response.data);
    } catch (err) {
      console.error('Error loading providers:', err);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await devicesAPI.getAllBulk();
      setDevices(response.data || []);
    } catch (err) {
      console.error('Error loading devices:', err);
    }
  };

  const handleOpenDialog = (sim = null) => {
    if (sim) {
      setEditingSim(sim);
      // Find provider and device objects from their IDs
      const providerObj = providers.find(p => p.id === sim.provider);
      const deviceObj = devices.find(d => d.id === sim.device);
      
      setFormData({
        iccid: sim.iccid,
        sim_number: sim.sim_number,
        provider: providerObj || null,
        status: sim.status || 'available',
        device: deviceObj || null,
        notes: sim.notes || '',
      });
    } else {
      setEditingSim(null);
      setFormData({
        iccid: '',
        sim_number: '',
        provider: null,
        status: 'available',
        device: null,
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSim(null);
  };

  const handleSubmit = async () => {
    try {
      // Convert objects to IDs for API
      const submitData = {
        ...formData,
        provider: formData.provider ? formData.provider.id : null,
        device: formData.device ? formData.device.id : null,
      };
      
      if (editingSim) {
        await simsAPI.update(editingSim.id, submitData);
        showSnackbar('SIM card updated successfully!', 'success');
      } else {
        await simsAPI.create(submitData);
        showSnackbar('SIM card created successfully!', 'success');
      }
      handleCloseDialog();
      loadSims();
    } catch (err) {
      console.error('Error saving SIM:', err);
      showSnackbar('Error saving SIM card: ' + (err.response?.data?.message || err.message), 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this SIM card?')) {
      try {
        await simsAPI.delete(id);
        showSnackbar('SIM card deleted successfully!', 'success');
        loadSims();
      } catch (err) {
        console.error('Error deleting SIM:', err);
        showSnackbar('Error deleting SIM card', 'error');
      }
    }
  };

  const getStatusOptions = () => {
    const allOptions = [
      { value: 'available', label: 'Available' },
      { value: 'assigned', label: 'Assigned' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'damaged', label: 'Damaged' },
      { value: 'lost', label: 'Lost' },
    ];
    
    // If a device is selected, hide "available" and show "assigned"
    if (formData.device) {
      return allOptions.filter(option => option.value !== 'available');
    }
    
    // If no device is selected, hide "assigned" and show "available"
    return allOptions.filter(option => option.value !== 'assigned');
  };

  const handleDeviceChange = (event, newValue) => {
    setFormData({ ...formData, device: newValue });
    
    // Auto-update status based on device selection
    if (newValue) {
      // Device selected - set status to assigned if it was available
      if (formData.status === 'available') {
        setFormData(prev => ({ ...prev, device: newValue, status: 'assigned' }));
      }
    } else {
      // Device cleared - set status to available if it was assigned
      if (formData.status === 'assigned') {
        setFormData(prev => ({ ...prev, device: newValue, status: 'available' }));
      }
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';
    
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  };

  // Display sims directly - backend handles pagination
  const displayedSims = sims;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'success';
      case 'assigned': return 'primary';
      case 'inactive': return 'default';
      case 'damaged': return 'error';
      case 'lost': return 'warning';
      default: return 'default';
    }
  };

  // Template Download
  const handleDownloadTemplate = async (format) => {
    try {
      const response = await simsAPI.downloadTemplate(format);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sims_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(`Template downloaded successfully (${format.toUpperCase()})`, 'success');
    } catch (err) {
      console.error('Error downloading template:', err);
      showSnackbar('Error downloading template', 'error');
    }
    setTemplateAnchorEl(null);
  };

  // Import Data with Progress
  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const response = await simsAPI.importData(file);
      const { job_id, total_rows } = response.data;
      
      setImportJobId(job_id);
      setImportDialogOpen(true);
      showSnackbar(`Import started: ${total_rows} rows to process`, 'info');
    } catch (err) {
      console.error('Error starting import:', err);
      showSnackbar('Import error: ' + (err.response?.data?.error || err.message), 'error');
    }
    setImportAnchorEl(null);
    event.target.value = '';
  };

  const handleImportComplete = (jobData) => {
    const { created_count, updated_count, errors } = jobData;
    let message = `Import completed: ${created_count} created, ${updated_count} updated`;
    if (errors && errors.length > 0) {
      message += `. ${errors.length} errors.`;
    }
    showSnackbar(message, errors?.length > 0 ? 'warning' : 'success');
    loadSims();
  };

  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
    setImportJobId(null);
  };

  // Export Data
  const handleExport = async (format) => {
    try {
      const response = await simsAPI.export(format);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sims_export.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(`Data exported successfully (${format.toUpperCase()})`, 'success');
    } catch (err) {
      console.error('Error exporting data:', err);
      showSnackbar('Error exporting data', 'error');
    }
    setExportAnchorEl(null);
  };

  if (loading && sims.length === 0) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          SIM Cards Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadSims}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={(e) => setTemplateAnchorEl(e.currentTarget)}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={(e) => setImportAnchorEl(e.currentTarget)}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={(e) => setExportAnchorEl(e.currentTarget)}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New SIM Card
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by ICCID, SIM number or provider..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Provider</InputLabel>
          <Select
            value={providerFilter}
            label="Provider"
            onChange={(e) => {
              setProviderFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Providers</MenuItem>
            {providers.map((provider) => (
              <MenuItem key={provider.id} value={provider.id}>
                {provider.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="available">Available</MenuItem>
            <MenuItem value="assigned">Assigned</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="damaged">Damaged</MenuItem>
            <MenuItem value="lost">Lost</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'iccid'}
                  direction={orderBy === 'iccid' ? order : 'asc'}
                  onClick={() => handleRequestSort('iccid')}
                >
                  <strong>ICCID</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'sim_number'}
                  direction={orderBy === 'sim_number' ? order : 'asc'}
                  onClick={() => handleRequestSort('sim_number')}
                >
                  <strong>SIM Number</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'provider_name'}
                  direction={orderBy === 'provider_name' ? order : 'asc'}
                  onClick={() => handleRequestSort('provider_name')}
                >
                  <strong>Provider</strong>
                </TableSortLabel>
              </TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Device</strong></TableCell>
              <TableCell><strong>Notes</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedSims.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No SIM cards found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedSims.map((sim) => (
                <TableRow key={sim.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{sim.iccid}</TableCell>
                  <TableCell>{sim.sim_number}</TableCell>
                  <TableCell>
                    <Chip label={sim.provider_name || 'N/A'} color="primary" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={sim.status_display || sim.status} 
                      color={getStatusColor(sim.status)} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    {sim.device_imei ? (
                      <Chip label={sim.device_imei} color="info" size="small" />
                    ) : (
                      <Chip label="None" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {sim.notes ? (
                      <Tooltip title={sim.notes}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {sim.notes}
                        </Typography>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(sim)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(sim.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
      />

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Chip label={`Total SIMs: ${sims.length}`} color="primary" size="small" />
        <Chip label={`Available: ${sims.filter(s => s.status === 'available').length}`} color="success" size="small" />
        <Chip label={`Assigned: ${sims.filter(s => s.status === 'assigned').length}`} color="info" size="small" />
        <Chip label={`Inactive: ${sims.filter(s => s.status === 'inactive').length}`} color="default" size="small" />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSim ? 'Edit SIM Card' : 'New SIM Card'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="ICCID *"
              value={formData.iccid}
              onChange={(e) => setFormData({ ...formData, iccid: e.target.value })}
              fullWidth
              required
              helperText="Unique identifier for the SIM card"
              disabled={!!editingSim}
            />

            <TextField
              label="SIM Number *"
              value={formData.sim_number}
              onChange={(e) => setFormData({ ...formData, sim_number: e.target.value })}
              fullWidth
              required
              helperText="Phone number associated with the SIM"
            />

            <Autocomplete
              fullWidth
              options={providers}
              getOptionLabel={(option) => option.name || ''}
              value={formData.provider}
              onChange={(event, newValue) => setFormData({ ...formData, provider: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Provider *"
                  required
                  helperText="Select the mobile network provider"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              filterOptions={createFilterOptions({
                matchFrom: 'any',
                stringify: (option) => option.name + ' ' + option.code,
              })}
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                {getStatusOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              fullWidth
              options={devices}
              getOptionLabel={(option) => `${option.imei} - ${option.model || 'Unknown model'}`}
              value={formData.device}
              onChange={handleDeviceChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Device (Optional)"
                  helperText="Assign this SIM to a device (optional)"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              filterOptions={createFilterOptions({
                matchFrom: 'any',
                stringify: (option) => option.imei + ' ' + (option.model || '') + ' ' + (option.status || ''),
              })}
            />

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
              helperText="Additional information about this SIM card"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.iccid || !formData.sim_number || !formData.provider}
          >
            {editingSim ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Download Menu */}
      <Menu
        anchorEl={templateAnchorEl}
        open={Boolean(templateAnchorEl)}
        onClose={() => setTemplateAnchorEl(null)}
      >
        <MenuItem onClick={() => handleDownloadTemplate('xlsx')}>
          <CloudUploadIcon sx={{ mr: 1 }} /> Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={() => handleDownloadTemplate('csv')}>
          <CloudUploadIcon sx={{ mr: 1 }} /> CSV (.csv)
        </MenuItem>
        <MenuItem onClick={() => handleDownloadTemplate('json')}>
          <CloudUploadIcon sx={{ mr: 1 }} /> JSON (.json)
        </MenuItem>
      </Menu>

      {/* Import Menu */}
      <Menu
        anchorEl={importAnchorEl}
        open={Boolean(importAnchorEl)}
        onClose={() => setImportAnchorEl(null)}
      >
        <MenuItem component="label">
          <UploadIcon sx={{ mr: 1 }} /> Excel (.xlsx)
          <input
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={handleImportFile}
          />
        </MenuItem>
        <MenuItem component="label">
          <UploadIcon sx={{ mr: 1 }} /> CSV (.csv)
          <input
            type="file"
            hidden
            accept=".csv"
            onChange={handleImportFile}
          />
        </MenuItem>
        <MenuItem component="label">
          <UploadIcon sx={{ mr: 1 }} /> JSON (.json)
          <input
            type="file"
            hidden
            accept=".json"
            onChange={handleImportFile}
          />
        </MenuItem>
      </Menu>

      {/* Export Menu */}
      <Menu
        anchorEl={exportAnchorEl}
        open={Boolean(exportAnchorEl)}
        onClose={() => setExportAnchorEl(null)}
      >
        <MenuItem onClick={() => handleExport('xlsx')}>
          <DownloadIcon sx={{ mr: 1 }} /> Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>
          <DownloadIcon sx={{ mr: 1 }} /> CSV (.csv)
        </MenuItem>
        <MenuItem onClick={() => handleExport('json')}>
          <DownloadIcon sx={{ mr: 1 }} /> JSON (.json)
        </MenuItem>
      </Menu>

      {/* Import Progress Dialog */}
      <ImportProgressDialog
        open={importDialogOpen}
        jobId={importJobId}
        getStatusFn={simsAPI.getImportStatus}
        onComplete={handleImportComplete}
        onClose={handleImportDialogClose}
        title="Import des cartes SIM..."
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default SimsPage;
